import { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "@gatsbyjs/reach-router";

import { applyIgPrices } from "../helpers/intelligems";
import { LuxeContext } from "../context/LuxeProvider";

import { withALErrorBoundary } from "../helpers/ErrorBoundary/ALErrorBoundary";
import { getCookie } from "@helpers/browser";
import { cn } from "@helpers/general";
import { isBannedProduct } from "@helpers/product";
import useDiscountsInfo, { DISCOUNT_INFO_MODES } from "../hooks/useDiscountsInfo";
import { ALSkeleton } from "./ALComponents";
import LuxePrice from "./priceContainer/LuxePrice";
import DiscountedPrice from "./priceContainer/DiscountedPrice";
import GiftWithPurchasePrice from "./priceContainer/GiftWithPurchasePrice";
import FullPrice from "./priceContainer/FullPrice";
import LineItemPrice from "./priceContainer/LineItemPrice";

import { ComponentType } from "../constants/ComponentType";
import DISCOUNT_TYPES from "../constants/DiscountTypes";

const LUXE_EXCLUDED_COMPONENTS = [
  ComponentType.MTO_APP,
  ComponentType.PDP_ADS,
  ComponentType.CP_ADS,
  ComponentType.FLOATING_ATC_ADS,
  ComponentType.PDP_QUICK_UPSELL,
];

function PriceContainer({
  productId,
  selectedVariant,
  prices,
  pricesLoading,
  variantPrices = null,
  componentType,
  tags = [],
  optionalProductsTotalPrice = 0,
  isFree = false,
  tierDiscountPercent,
}) {
  const { luxe } = useContext(LuxeContext);

  const location = useLocation();
  const discountsInfo = useDiscountsInfo();
  const luxeDiscountsInfo = useDiscountsInfo(DISCOUNT_INFO_MODES.LUXE_ONLY);
  const giftDiscount =
    (discountsInfo || []).find((s) => s.type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE) || null;

  const filteredLuxeDiscountsInfo = useMemo(
    () =>
      luxeDiscountsInfo.filter((discount) => discount.type !== DISCOUNT_TYPES.GIFT_WITH_PURCHASE),
    [luxeDiscountsInfo]
  );

  const isProductExcludedFromLuxe = isBannedProduct(tags, filteredLuxeDiscountsInfo);

  const isGiftProduct =
    giftDiscount?.freegift?.products?.some(
      (giftProduct) => giftProduct?.shopify?.shopifyId === productId
    ) || isFree;

  const isPdpAds = location.pathname.includes("/p/");

  const { isLuxeActive, hasLuxeInCart, shouldAddLuxeInNextATC } = luxe;
  const isLuxe = isLuxeActive || hasLuxeInCart || shouldAddLuxeInNextATC;
  const isMtoApp = componentType === ComponentType.MTO_APP;

  const disableSubscriptionCookie = getCookie("subscription_disabled");
  const shouldDisableLuxe = !!disableSubscriptionCookie && !isLuxeActive;

  const showLuxePrices =
    luxe.isLuxeEnabled &&
    !shouldDisableLuxe &&
    !isProductExcludedFromLuxe &&
    !LUXE_EXCLUDED_COMPONENTS.includes(componentType) &&
    !luxe.isForcedGuest &&
    !isGiftProduct &&
    !isPdpAds &&
    !isMtoApp &&
    (componentType !== ComponentType.SIDE_CART_UPSELL || isLuxe); // for side cart upsell, we only show luxe prices if the user is luxe

  if (componentType === ComponentType.SIDE_CART) {
    return (
      <LineItemPrice variant={selectedVariant} isLuxe={isLuxe} isGiftProduct={isGiftProduct} />
    );
  }

  const newPrices = variantPrices || prices;

  const pId = productId.replace("gid://shopify/Product/", "");
  const { hasDiscount, finalPrice, fullPrice } = applyIgPrices(pId, selectedVariant, newPrices);

  const finalFullPrice = parseFloat(fullPrice) + optionalProductsTotalPrice;
  const finalFinalPrice = parseFloat(finalPrice) + optionalProductsTotalPrice;

  const isStartingAt =
    !prices?.variants?.every(
      (p) => parseFloat(p.final_price) === parseFloat(prices?.final_price)
    ) &&
    (componentType === ComponentType.CP ||
      componentType === ComponentType.PDP ||
      componentType === ComponentType.PDP_ADS) &&
    parseFloat(prices?.final_price) === parseFloat(prices?.variants?.[0]?.final_price);

  const wrapperClass = cn("mt-0", {
    "typo-h3 mobile:typo-h2":
      componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS,
    "flex-col gap-0":
      hasDiscount &&
      (componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS),
    "flex gap-[6px] mobile:gap-[5px]":
      componentType !== ComponentType.PDP &&
      componentType !== ComponentType.PDP_ADS &&
      hasDiscount &&
      !isGiftProduct,
  });

  if (pricesLoading) {
    return (
      <div
        className={cn({
          "height-[22px]":
            componentType === ComponentType.CP || componentType === ComponentType.CP_ADS,
        })}
      >
        <ALSkeleton width="31px" height="18px" />
      </div>
    );
  }

  if (!prices || prices?.price === undefined) {
    return null;
  }

  return (
    <div
      data-class="root"
      className={cn("flex flex-col", {
        "items-start mobile:items-end mb-[30px] mobile:mb-0":
          componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS,

        "items-end":
          componentType === ComponentType.FLOATING_ATC ||
          componentType === ComponentType.FLOATING_ATC_ADS ||
          componentType === ComponentType.SIDE_CART_UPSELL,
      })}
    >
      <div
        data-class="wrapper"
        className={cn(wrapperClass, {
          "pt-[20px] mobile:pt-0":
            componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS,

          "flex flex-row gap-[8px] mobile:gap-[6px]":
            showLuxePrices &&
            !isGiftProduct &&
            fullPrice &&
            (componentType === ComponentType.PDP || componentType === ComponentType.PDP_ADS),
        })}
      >
        {!isGiftProduct && fullPrice && (
          <FullPrice
            componentType={componentType}
            price={finalFullPrice}
            showLuxePrices={showLuxePrices}
            hasDiscount={hasDiscount}
            isStartingAt={isStartingAt}
          />
        )}

        {isGiftProduct && <GiftWithPurchasePrice componentType={componentType} price={fullPrice} />}

        {hasDiscount && finalPrice && !isGiftProduct && (
          <DiscountedPrice
            componentType={componentType}
            price={finalFinalPrice}
            isLuxeEnabled={luxe.isLuxeEnabled}
          />
        )}
      </div>

      {showLuxePrices && (
        <LuxePrice
          componentType={componentType}
          price={finalFinalPrice}
          tags={tags}
          tierDiscountPercent={tierDiscountPercent}
        />
      )}
    </div>
  );
}

PriceContainer.propTypes = {
  componentType: PropTypes.string,
  optionalProductsTotalPrice: PropTypes.number,
  tags: PropTypes.arrayOf(PropTypes.string),
  prices: PropTypes.object,
  pricesLoading: PropTypes.bool,
  productId: PropTypes.string,
  variantPrices: PropTypes.object,
  selectedVariant: PropTypes.object,
  isFree: PropTypes.bool,
  tierDiscountPercent: PropTypes.number,
};

export default withALErrorBoundary({
  name: "PriceContainer",
  priority: "P1",
})(PriceContainer);
